// 天气和地理位置服务

const WEATHER_API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY || 'demo_key'
const WEATHER_API_URL = 'https://api.openweathermap.org/data/2.5'

// 获取用户当前位置
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    // 检查是否支持地理位置API
    if (!navigator.geolocation) {
      console.log('浏览器不支持地理位置API，使用默认位置')
      resolve({
        lat: 22.5431,
        lng: 114.0579,
        city: '深圳',
        name: '深圳市'
      })
      return
    }

    // 检查是否是HTTPS环境（地理位置API要求）
    if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
      console.log('非安全环境，无法使用地理位置API，使用默认位置')
      resolve({
        lat: 22.5431,
        lng: 114.0579,
        city: '深圳',
        name: '深圳市'
      })
      return
    }

    const options = {
      enableHighAccuracy: false, // 降低精度要求，提高成功率
      timeout: 5000, // 减少超时时间
      maximumAge: 600000 // 10分钟缓存
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords
        console.log('获取地理位置成功:', lat, lng)
        
        try {
          // 尝试获取城市名称
          const cityName = await getCityName(lat, lng)
          resolve({
            lat,
            lng,
            city: cityName,
            name: cityName
          })
        } catch (error) {
          // 即使无法获取城市名称，也返回坐标
          resolve({
            lat,
            lng,
            city: `${lat.toFixed(2)}, ${lng.toFixed(2)}`,
            name: '当前位置'
          })
        }
      },
      (error) => {
        console.log('地理位置获取失败，使用默认位置。错误:', error.message)
        // 根据错误类型给出友好提示
        let fallbackCity = '深圳'
        switch (error.code) {
          case error.PERMISSION_DENIED:
            console.log('用户拒绝了地理位置请求')
            break
          case error.POSITION_UNAVAILABLE:
            console.log('位置信息不可用')
            break
          case error.TIMEOUT:
            console.log('获取位置信息超时')
            break
          default:
            console.log('获取位置信息时发生未知错误')
            break
        }
        
        // 返回默认位置（深圳）
        resolve({
          lat: 22.5431,
          lng: 114.0579,
          city: fallbackCity,
          name: fallbackCity + '市'
        })
      },
      options
    )
  })
}

// 通过坐标获取城市名称
export const getCityName = async (lat, lng) => {
  try {
    const response = await fetch(
      `${WEATHER_API_URL}/weather?lat=${lat}&lon=${lng}&appid=${WEATHER_API_KEY}&lang=zh_cn`
    )
    
    if (!response.ok) {
      throw new Error('Weather API request failed')
    }
    
    const data = await response.json()
    return data.name || '未知城市'
  } catch (error) {
    console.error('Error getting city name:', error)
    return '未知位置'
  }
}

// 获取天气数据
export const getWeatherData = async (lat, lng) => {
  try {
    // 检查是否有有效的API密钥
    if (!WEATHER_API_KEY || WEATHER_API_KEY === 'demo_key') {
      console.log('没有有效的天气API密钥，使用模拟天气数据')
      return generateMockWeatherData(lat, lng)
    }

    const response = await fetch(
      `${WEATHER_API_URL}/weather?lat=${lat}&lon=${lng}&appid=${WEATHER_API_KEY}&units=metric&lang=zh_cn`
    )

    if (!response.ok) {
      if (response.status === 401) {
        console.log('天气API密钥无效，使用模拟天气数据')
      } else {
        console.log('天气API请求失败，使用模拟天气数据')
      }
      throw new Error('Weather API request failed')
    }

    const data = await response.json()

    return {
      temperature: data.main.temp,
      condition: data.weather[0].main,
      description: data.weather[0].description,
      humidity: data.main.humidity,
      windSpeed: data.wind?.speed || 0,
      pressure: data.main.pressure,
      visibility: data.visibility,
      icon: data.weather[0].icon,
      city: data.name,
      country: data.sys.country
    }
  } catch (error) {
    console.log('获取天气数据失败，使用模拟数据:', error.message)
    return generateMockWeatherData(lat, lng)
  }
}

// 生成模拟天气数据
function generateMockWeatherData(lat, lng) {
  // 根据位置和时间生成合理的模拟天气
  const hour = new Date().getHours()
  const isDay = hour >= 6 && hour < 18
  
  // 根据位置大致判断气候
  const isNorth = lat > 35
  const isTropical = lat < 25 && lat > -25
  
  const weatherConditions = [
    { condition: 'Clear', description: '晴朗', temp: isTropical ? 28 : (isNorth ? 15 : 22) },
    { condition: 'Clouds', description: '多云', temp: isTropical ? 26 : (isNorth ? 12 : 20) },
    { condition: 'Rain', description: '小雨', temp: isTropical ? 24 : (isNorth ? 10 : 18) }
  ]
  
  const selectedWeather = weatherConditions[Math.floor(Math.random() * weatherConditions.length)]
  
  return {
    temperature: selectedWeather.temp + (Math.random() * 6 - 3), // ±3度随机
    condition: selectedWeather.condition,
    description: selectedWeather.description,
    humidity: 50 + Math.floor(Math.random() * 40), // 50-90%
    windSpeed: Math.random() * 10, // 0-10 m/s
    pressure: 1000 + Math.floor(Math.random() * 40), // 1000-1040 hPa
    visibility: 10000,
    icon: isDay ? '01d' : '01n',
    city: '示例城市',
    country: 'CN'
  }
}

// 根据天气条件获取音乐风格建议
export const getWeatherMoodMapping = (weather) => {
  if (!weather) return ['流行', '轻松']

  const { condition, temperature } = weather
  
  const mappings = {
    'Clear': temperature > 25 ? ['欢快', '流行', '夏日'] : ['轻松', '温暖', '舒缓'],
    'Clouds': ['安静', '思考', '爵士'],
    'Rain': ['忧郁', '安静', '古典', '钢琴'],
    'Drizzle': ['浪漫', '柔和', '民谣'],
    'Snow': ['宁静', '古典', '冬日'],
    'Thunderstorm': ['激烈', '摇滚', '电子'],
    'Mist': ['神秘', '环境音乐', '冥想'],
    'Fog': ['朦胧', '爵士', '蓝调']
  }

  return mappings[condition] || ['流行', '轻松']
}

// 获取天气相关的颜色主题
export const getWeatherColors = (weather) => {
  if (!weather) return ['purple', 'blue']

  const { condition } = weather
  
  const colorMappings = {
    'Clear': ['yellow', 'orange'],
    'Clouds': ['gray', 'blue'],
    'Rain': ['blue', 'indigo'],
    'Drizzle': ['blue', 'teal'],
    'Snow': ['white', 'blue'],
    'Thunderstorm': ['purple', 'gray'],
    'Mist': ['gray', 'purple'],
    'Fog': ['gray', 'blue']
  }

  return colorMappings[condition] || ['purple', 'blue']
}
